<template>
  <b-modal ref="modal" size="xl" hide-header hide-footer centered scrollable
           body-class="p-0" content-class="cim-content" @hidden="onHidden">
    <div v-if="loading && !insight" class="cim-loading">
      <b-spinner variant="primary"/>
      <div class="mt-75">{{ $t('operations.loading') }}</div>
    </div>

    <div v-else-if="insight" class="cim" :class="{ 'cim--dark': isDarkSkin }">

      <!-- ============ HERO ============ -->
      <div class="cim-hero">
        <div class="cim-hero__medal" v-if="rank">
          <span v-if="rank === 1">🥇</span><span v-else-if="rank === 2">🥈</span>
          <span v-else-if="rank === 3">🥉</span><span v-else>#{{ rank }}</span>
        </div>
        <div class="cim-hero__main">
          <div class="cim-hero__name">
            {{ c.fullName }}
            <i v-if="c.vip" class="fas fa-star cim-vip" v-b-tooltip title="VIP"></i>
          </div>
          <div class="cim-hero__sub">
            <span v-if="c.country"><i class="fas fa-location-dot mr-25"></i>{{ c.country }}<span v-if="c.city">, {{ c.city }}</span></span>
            <span v-if="c.affiliateName" class="ml-1"><i class="fas fa-handshake mr-25"></i>{{ c.affiliateName }}</span>
            <span v-if="c.campaign" class="ml-1"><i class="fas fa-bullhorn mr-25"></i>{{ c.campaign }}</span>
          </div>
          <div class="cim-hero__badges">
            <span class="cim-tag" v-if="getOrganizationNameById(c.orgId)">
              <i class="fas fa-building mr-25"></i>{{ getOrganizationNameById(c.orgId) }}
            </span>
            <span class="cim-tag" v-if="getBusinessUnitNameById(c.businessUnitId)">
              <i class="fas fa-sitemap mr-25"></i>{{ getBusinessUnitNameById(c.businessUnitId) }}
            </span>
            <span class="cim-tag" v-if="c.creationTime">
              <i class="fas fa-cake-candles mr-25"></i>{{ $t('clientInsights.clientSince') }} {{ c.creationTime | momentDate }}
            </span>
            <span class="cim-tag" v-if="c.email"><i class="fas fa-envelope mr-25"></i>{{ c.email }}</span>
            <span class="cim-tag" v-if="c.phone"><i class="fas fa-phone mr-25"></i>{{ c.phone }}</span>
          </div>
        </div>
        <div class="cim-hero__actions">
          <button type="button" class="cim-open-btn" @click="openClientCard">
            <span class="cim-open-btn__icon"><i class="fas fa-user"></i></span>
            <span class="cim-open-btn__label">{{ $t('clientInsights.openClient') }}</span>
            <i class="fas fa-arrow-up-right-from-square cim-open-btn__go"></i>
          </button>
          <b-link class="cim-hero__close" @click="$refs.modal.hide()"><feather-icon icon="XIcon" size="20"/></b-link>
        </div>
      </div>

      <div class="cim-body">

        <!-- ============ DATE FILTER (scopes the monetary stats & drills) ============ -->
        <div class="cim-range">
          <calendar-bar v-model="dateRange" @input="onRangeChanged"/>
        </div>

       <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">
        <!-- ============ KPI TILES ============ -->
        <div class="cim-kpis">
          <div class="cim-kpi cim-kpi--dep cim-kpi--click"
               @click="drill($t('clientInsights.totalDeposited'), depositDrillFilters())">
            <div class="cim-kpi__label">{{ $t('clientInsights.totalDeposited') }}</div>
            <div class="cim-kpi__value">{{ money(m.deposits.total) }}</div>
            <div class="cim-kpi__foot">{{ m.deposits.count }} {{ $t('clientInsights.deposits') }}</div>
            <i class="fas fa-expand cim-kpi__expand"></i>
          </div>
          <div class="cim-kpi cim-kpi--wd cim-kpi--click"
               @click="drill($t('clientInsights.totalWithdrawn'), withdrawDrillFilters())">
            <div class="cim-kpi__label">{{ $t('clientInsights.totalWithdrawn') }}</div>
            <div class="cim-kpi__value">{{ money(m.withdrawals.total) }}</div>
            <div class="cim-kpi__foot">{{ m.withdrawals.count }} {{ $t('clientInsights.withdrawals') }}</div>
            <i class="fas fa-expand cim-kpi__expand"></i>
          </div>
          <div class="cim-kpi cim-kpi--net cim-kpi--click"
               @click="drill($t('clientInsights.net'), netDrillFilters())">
            <div class="cim-kpi__label">{{ $t('clientInsights.net') }}</div>
            <div class="cim-kpi__value">{{ money(m.net) }}</div>
            <div class="cim-kpi__foot">{{ $t('clientInsights.bonus') }}: {{ money(m.bonus.total) }}</div>
            <i class="fas fa-expand cim-kpi__expand"></i>
          </div>
          <div class="cim-kpi cim-kpi--avg cim-kpi--click"
               @click="drill($t('clientInsights.avgDeposit'), depositDrillFilters())">
            <div class="cim-kpi__label">{{ $t('clientInsights.avgDeposit') }}</div>
            <div class="cim-kpi__value">{{ money(m.deposits.avg) }}</div>
            <div class="cim-kpi__foot">{{ $t('clientInsights.biggest') }}: {{ money(m.deposits.max) }}</div>
            <i class="fas fa-expand cim-kpi__expand"></i>
          </div>
          <div class="cim-kpi cim-kpi--bal">
            <div class="cim-kpi__label">{{ $t('clientInsights.balanceNow') }}</div>
            <div class="cim-kpi__value">{{ money(c.totalBalance) }}</div>
            <div class="cim-kpi__foot">{{ $t('clientInsights.credit') }}: {{ money(c.totalCredit) }}</div>
          </div>
        </div>

        <b-row>
          <!-- ============ JOURNEY ============ -->
          <b-col lg="5" md="12">
            <div class="cim-panel">
              <div class="cim-panel__title"><i class="fas fa-route mr-50"></i>{{ $t('clientInsights.journey') }}</div>
              <div class="cim-journey">
                <div v-for="(step, i) in journey" :key="i" class="cim-step">
                  <div class="cim-step__dot" :style="{ background: step.color }">
                    <i :class="step.icon"></i>
                  </div>
                  <div class="cim-step__body">
                    <div class="cim-step__label">
                      {{ step.label }}
                      <span v-if="step.extra" class="cim-step__extra">{{ step.extra }}</span>
                    </div>
                    <div class="cim-step__time">
                      {{ step.time | momentDateTime }}
                      <span v-if="step.delta" class="cim-step__delta">{{ step.delta }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ============ PEOPLE ============ -->
            <div class="cim-panel">
              <div class="cim-panel__title"><i class="fas fa-people-group mr-50"></i>{{ $t('clientInsights.team') }}</div>
              <div class="cim-people">
                <div class="cim-person" v-if="c.ftdOwnerId">
                  <div class="cim-person__avatar cim-person__avatar--sales">{{ initials(userName(c.ftdOwnerId)) }}</div>
                  <div>
                    <div class="cim-person__role">{{ $t('clientInsights.salesOwner') }}</div>
                    <div class="cim-person__name">{{ userName(c.ftdOwnerId) }}</div>
                  </div>
                </div>
                <div class="cim-person" v-if="c.representativeUserId">
                  <div class="cim-person__avatar cim-person__avatar--ret">{{ initials(userName(c.representativeUserId)) }}</div>
                  <div>
                    <div class="cim-person__role">{{ $t('clientInsights.retentionOwner') }}</div>
                    <div class="cim-person__name">{{ userName(c.representativeUserId) }}</div>
                  </div>
                </div>
                <div class="cim-person" v-if="ftdApprover">
                  <div class="cim-person__avatar cim-person__avatar--fin">{{ initials(userName(ftdApprover)) }}</div>
                  <div>
                    <div class="cim-person__role">{{ $t('clientInsights.ftdApprovedBy') }}</div>
                    <div class="cim-person__name">{{ userName(ftdApprover) }}</div>
                  </div>
                </div>
              </div>

              <!-- Depositor split -->
              <div class="cim-panel__subtitle" v-if="m.byDepositor.length">{{ $t('clientInsights.whoDeposited') }}</div>
              <div v-for="d in m.byDepositor.slice(0, 5)" :key="'dep-' + d.userId" class="cim-split">
                <div class="cim-split__name">{{ userName(d.userId) }}</div>
                <div class="cim-split__barwrap"><div class="cim-split__bar" :style="{ width: splitWidth(d) }"></div></div>
                <div class="cim-split__val">{{ money(d.amount) }} <small>({{ d.count }})</small></div>
              </div>

              <div class="cim-panel__subtitle" v-if="m.byApprover.length">{{ $t('clientInsights.whoApproved') }}</div>
              <div v-for="a in m.byApprover.slice(0, 5)" :key="'app-' + a.userId" class="cim-split">
                <div class="cim-split__name">{{ userName(a.userId) }}</div>
                <div class="cim-split__barwrap"><div class="cim-split__bar cim-split__bar--green" :style="{ width: splitWidth(a, m.byApprover) }"></div></div>
                <div class="cim-split__val">{{ money(a.amount) }} <small>({{ a.count }})</small></div>
              </div>
            </div>

            <!-- ============ CONTACT STATS ============ -->
            <div class="cim-panel">
              <div class="cim-panel__title"><i class="fas fa-headset mr-50"></i>{{ $t('clientInsights.reachability') }}</div>
              <div class="cim-mini-stats">
                <div class="cim-mini">
                  <div class="cim-mini__value">{{ bestHourLabel }}</div>
                  <div class="cim-mini__label">{{ $t('clientInsights.bestTime') }}</div>
                </div>
                <div class="cim-mini">
                  <div class="cim-mini__value">{{ calls.total }}</div>
                  <div class="cim-mini__label">{{ $t('clientInsights.callsMade') }}
                    <span class="text-success">{{ calls.success }} ✓</span> / <span class="text-danger">{{ calls.failed }} ✗</span>
                  </div>
                </div>
                <div class="cim-mini">
                  <div class="cim-mini__value">{{ logins.total }}</div>
                  <div class="cim-mini__label">{{ $t('clientInsights.logins') }}</div>
                </div>
                <div class="cim-mini" v-if="c.lastConnection">
                  <div class="cim-mini__value">{{ c.lastConnection | momentDate }}</div>
                  <div class="cim-mini__label">{{ $t('clientInsights.lastSeen') }}</div>
                </div>
              </div>
            </div>
          </b-col>

          <!-- ============ CHARTS + NOTES ============ -->
          <b-col lg="7" md="12">
            <div class="cim-panel" v-if="monthlySeries.length">
              <div class="cim-panel__title"><i class="fas fa-chart-column mr-50"></i>{{ $t('clientInsights.monthlyActivity') }}</div>
              <apexchart type="bar" height="230" :options="monthlyChartOptions" :series="monthlyChartSeries"/>
            </div>

            <div class="cim-panel">
              <div class="cim-panel__title"><i class="fas fa-clock mr-50"></i>{{ $t('clientInsights.activityByHour') }}</div>
              <apexchart type="bar" height="200" :options="hourChartOptions" :series="hourChartSeries"/>
            </div>

            <div class="cim-panel">
              <div class="cim-panel__title">
                <i class="fas fa-note-sticky mr-50"></i>{{ $t('clientInsights.latestNotes') }}
                <span class="cim-panel__count" v-if="c.totalNotes">{{ c.totalNotes }}</span>
              </div>
              <div v-if="!notes.length" class="text-muted font-small-3 py-50">{{ $t('clientInsights.noNotes') }}</div>
              <div v-else class="cim-notes-scroll">
                <div v-for="(note, i) in notes" :key="'note-' + i" class="cim-note">
                  <div class="cim-note__head">
                    <span class="cim-note__author"><i class="fas fa-user-pen mr-25"></i>{{ userName(note.userId) }}</span>
                    <i v-if="note.isPinned" class="fas fa-thumbtack cim-note__flag" v-b-tooltip title="Pinned"></i>
                    <i v-if="note.isImportant" class="fas fa-circle-exclamation cim-note__flag cim-note__flag--imp" v-b-tooltip title="Important"></i>
                    <span class="cim-note__time">{{ note.time | momentDateTime }}</span>
                  </div>
                  <div class="cim-note__text">{{ note.text }}</div>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
       </b-overlay>
      </div>
    </div>

    <insight-transactions-modal ref="txModal"/>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import InsightTransactionsModal from '@/components/modal/InsightTransactionsModal';
import CalendarBar from '@/components/widget/CalendarBar';

const EMPTY_MONETARY = {
  deposits: { total: 0, count: 0, avg: 0, max: 0, first: null, last: null },
  withdrawals: { total: 0, count: 0 },
  bonus: { total: 0, count: 0 },
  net: 0,
  monthly: [],
  byDepositor: [],
  byApprover: [],
  ftd: null,
  std: null,
};

export default {
  name: 'ClientInsightModal',
  mixins: [mixinBase, mixinNotifications],
  components: { InsightTransactionsModal, CalendarBar },
  data: () => ({
    loading: false,
    clientId: null,
    dateRange: { from: null, until: null },
    lastRangeKey: null,
    rank: null,
    insight: null,
  }),
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['getUserFullName', 'getOrganizationNameById', 'getBusinessUnitNameById']),

    c() { return (this.insight && this.insight.client) || {}; },
    m() { return (this.insight && this.insight.monetary) || EMPTY_MONETARY; },
    calls() { return (this.insight && this.insight.calls) || { total: 0, success: 0, failed: 0, byHour: [], byAgent: [] }; },
    logins() { return (this.insight && this.insight.logins) || { total: 0, byHour: [], byWeekday: [] }; },
    notes() { return (this.insight && this.insight.notes) || []; },

    ftdApprover() { return this.m.ftd ? this.m.ftd.statusUserId : null; },

    journey() {
      const steps = [];
      const push = (time, label, icon, color, extra, deltaFrom) => {
        if (!time) return;
        let delta = null;
        if (deltaFrom) {
          const days = this.$moment(time).diff(this.$moment(deltaFrom), 'days');
          delta = days >= 0 ? `+${days} ${this.$t('clientInsights.days')}` : null;
        }
        steps.push({ time, label, icon, color, extra, delta });
      };
      // Client fields can be null on older records — fall back to the actual
      // FTD/STD transactions returned by the monetary aggregation.
      const ftdTime = this.c.ftdTime || (this.m.ftd && this.m.ftd.time);
      const ftdAmount = this.c.ftdAmount || (this.m.ftd && this.m.ftd.amount);
      const stdTime = this.c.stdTime || (this.m.std && this.m.std.time);
      const stdAmount = this.c.stdAmount || (this.m.std && this.m.std.amount);

      push(this.c.creationTime, this.$t('clientInsights.leadCreated'), 'fas fa-user-plus', '#7367f0');
      push(this.c.interestTime, this.$t('clientInsights.interest'), 'fas fa-fire', '#ff9f43', null, this.c.creationTime);
      push(ftdTime, this.$t('clientInsights.converted'),
          'fas fa-sack-dollar', '#28c76f',
          ftdAmount ? this.money(ftdAmount) : null, this.c.creationTime);
      push(stdTime, 'STD', 'fas fa-coins', '#00cfe8',
          stdAmount ? this.money(stdAmount) : null, ftdTime);
      push(this.m.deposits.last, this.$t('clientInsights.lastDeposit'), 'fas fa-arrow-down', '#4b4b9b');
      push(this.c.lastConnection, this.$t('clientInsights.lastSeen'), 'fas fa-eye', '#82868b');
      return steps;
    },

    combinedHours() {
      const out = new Array(24).fill(0);
      (this.logins.byHour || []).forEach((v, i) => { out[i] += v || 0; });
      (this.calls.byHour || []).forEach((v, i) => { out[i] += v || 0; });
      return out;
    },
    bestHourLabel() {
      const hours = this.combinedHours;
      const max = Math.max(...hours);
      if (!max) return '—';
      const h = hours.indexOf(max);
      return `${String(h).padStart(2, '0')}:00–${String((h + 1) % 24).padStart(2, '0')}:00`;
    },

    monthlySeries() { return this.m.monthly || []; },
    monthlyChartSeries() {
      return [
        { name: this.$t('clientInsights.deposits'), data: this.monthlySeries.map(p => Math.round(p.deposits)) },
        { name: this.$t('clientInsights.withdrawals'), data: this.monthlySeries.map(p => Math.round(Math.abs(p.withdrawals))) },
      ];
    },
    monthlyChartOptions() {
      return {
        chart: { toolbar: { show: false }, foreColor: this.isDarkSkin ? '#b4b7bd' : '#6e6b7b' },
        colors: ['#28c76f', '#ea5455'],
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.monthlySeries.map(p => p.month) },
        yaxis: { labels: { formatter: v => this.money(v) } },
        legend: { position: 'top' },
        grid: { borderColor: this.isDarkSkin ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
        tooltip: { theme: this.isDarkSkin ? 'dark' : 'light' },
      };
    },
    hourChartSeries() {
      return [
        { name: this.$t('clientInsights.logins'), data: this.logins.byHour || new Array(24).fill(0) },
        { name: this.$t('clientInsights.callsMade'), data: this.calls.byHour || new Array(24).fill(0) },
      ];
    },
    hourChartOptions() {
      const best = this.combinedHours.indexOf(Math.max(...this.combinedHours));
      return {
        chart: { toolbar: { show: false }, stacked: true, foreColor: this.isDarkSkin ? '#b4b7bd' : '#6e6b7b' },
        colors: ['#7367f0', '#00cfe8'],
        plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
        dataLabels: { enabled: false },
        xaxis: { categories: Array.from({ length: 24 }, (_, i) => `${i}h`), tickAmount: 12 },
        legend: { position: 'top' },
        grid: { borderColor: this.isDarkSkin ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' },
        tooltip: { theme: this.isDarkSkin ? 'dark' : 'light' },
        annotations: Math.max(...this.combinedHours) > 0 ? {
          xaxis: [{
            x: `${best}h`,
            borderColor: '#ff9f43',
            label: {
              text: this.$t('clientInsights.bestTime'),
              style: { background: '#ff9f43', color: '#fff' },
            },
          }],
        } : {},
      };
    },
  },
  methods: {
    ...mapActions('dashboard', ['getClientInsight']),

    show(clientId, rank, range) {
      this.clientId = clientId;
      this.rank = rank || null;
      this.dateRange = range && range.from
          ? { from: range.from, until: range.until }
          : { from: null, until: null };
      this.insight = null;
      this.$refs.modal.show();
      this.fetchInsight();
    },
    // CalendarBar inside the modal — refetch the monetary stats for the new period.
    // The lastRangeKey guard swallows the bar's initial mount echo.
    onRangeChanged() {
      if (!this.clientId || this.loading) return;
      const key = JSON.stringify({ from: this.dateRange.from, until: this.dateRange.until });
      if (key === this.lastRangeKey) return;
      this.fetchInsight();
    },
    fetchInsight() {
      this.loading = true;
      this.lastRangeKey = JSON.stringify({ from: this.dateRange.from, until: this.dateRange.until });
      const range = this.dateRange && this.dateRange.from
          ? { from: this.dateRange.from, until: this.dateRange.until }
          : null;
      this.getClientInsight({ clientId: this.clientId, range })
          .then(data => { this.insight = data; })
          .catch(err => {
            this.showServerErrorNotification('getClientInsight', 'clientInsights', err);
            this.$refs.modal.hide();
          })
          .finally(() => { this.loading = false; });
    },
    onHidden() { this.insight = null; this.clientId = null; },
    // Same period as the modal's calendar (empty = lifetime)
    rangeFilter() {
      if (!this.dateRange || !this.dateRange.from) return {};
      return {
        registrationTime: {
          start: this.$moment(this.dateRange.from).startOf('day').toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until || this.dateRange.from).endOf('day').toServerDateTimeFormat(),
        },
      };
    },
    openClientCard() {
      if (this.clientId) window.open(`/client/${this.clientId}`, '_blank');
    },

    // ---- Stat-card drill-down: open the raw transactions behind a KPI ----
    drill(title, filters) {
      this.$refs.txModal.show(`${this.c.fullName || ''} — ${title}`, filters);
    },
    depositDrillFilters() {
      return {
        clientDetails: { clientId: this.clientId },
        type: 'DEPOSIT',
        action: ['CREDIT_LINE'],
        status: ['APPROVED'],
        realType: ['REAL'],
        isTest: false,
        ...this.rangeFilter(),
      };
    },
    withdrawDrillFilters() {
      return {
        clientDetails: { clientId: this.clientId },
        type: 'WITHDRAWAL',
        status: ['APPROVED'],
        realType: ['REAL'],
        isTest: false,
        ...this.rangeFilter(),
      };
    },
    netDrillFilters() {
      // Deposits AND withdrawals, real money only
      return {
        clientDetails: { clientId: this.clientId },
        action: ['CREDIT_LINE'],
        status: ['APPROVED'],
        realType: ['REAL'],
        isTest: false,
        ...this.rangeFilter(),
      };
    },
    money(v) { return this.convertToEURO(v || 0); },
    userName(id) {
      if (!id) return '—';
      const name = this.getUserFullName(id);
      return name && name.trim() ? name : `#${id}`;
    },
    initials(name) {
      if (!name || name === '—') return '?';
      return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('');
    },
    splitWidth(row, list) {
      const source = list || this.m.byDepositor;
      const max = Math.max(...source.map(r => Math.abs(r.amount)), 1);
      return Math.max(4, Math.round((Math.abs(row.amount) / max) * 100)) + '%';
    },
  },
};
</script>

<style lang="scss">
.cim-content { border-radius: 18px; overflow: hidden; }
</style>

<style scoped lang="scss">
.cim-loading { padding: 4rem 1rem; text-align: center; color: #9a9dad; }

/* ===== Hero ===== */
.cim-hero {
  display: flex; align-items: center; gap: 1.1rem;
  padding: 1.4rem 1.5rem;
  background: linear-gradient(135deg, #2b2f55 0%, #4b4b9b 55%, #7367f0 100%);
  color: #fff;
  /* Stays visible while the modal body scrolls through long content (e.g. many notes) */
  position: sticky;
  top: 0;
  z-index: 20;
  box-shadow: 0 6px 18px -8px rgba(20, 24, 50, 0.45);

  &__medal { font-size: 2.6rem; line-height: 1; flex-shrink: 0; }
  &__main { flex: 1; min-width: 0; }
  &__name { font-size: 1.4rem; font-weight: 800; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__sub { font-size: 0.82rem; opacity: 0.88; margin-top: 0.15rem; }
  &__badges { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.6rem; }
  &__actions { display: flex; align-items: center; gap: 0.75rem; flex-shrink: 0; }
  &__close { color: rgba(255, 255, 255, 0.8); &:hover { color: #fff; } }
}
.cim-tag {
  display: inline-flex; align-items: center;
  background: rgba(255, 255, 255, 0.16); backdrop-filter: blur(4px);
  border-radius: 999px; padding: 0.22rem 0.65rem;
  font-size: 0.72rem; font-weight: 600;
}

/* ===== Open client button ===== */
.cim-open-btn {
  display: inline-flex; align-items: center; gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(8px);
  color: #fff;
  border-radius: 999px;
  padding: 0.4rem 1rem 0.4rem 0.45rem;
  font-weight: 700; font-size: 0.82rem;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &__icon {
    width: 28px; height: 28px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #ffd54a, #ff9f43);
    color: #4b3200; font-size: 0.78rem;
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.25);
    transition: transform 0.2s ease;
  }
  &__label { letter-spacing: 0.01em; }
  &__go { font-size: 0.7rem; opacity: 0.75; transition: transform 0.2s ease, opacity 0.2s ease; }

  &:hover {
    background: rgba(255, 255, 255, 0.26);
    border-color: rgba(255, 255, 255, 0.6);
    transform: translateY(-1px);
    box-shadow: 0 8px 20px -8px rgba(0, 0, 0, 0.45);

    .cim-open-btn__icon { transform: scale(1.08) rotate(-6deg); }
    .cim-open-btn__go { transform: translate(2px, -2px); opacity: 1; }
  }
  &:active { transform: translateY(0); }
}
.cim-vip { color: #ffd54a; font-size: 1rem; margin-left: 0.25rem; }

/* ===== Body ===== */
.cim-body { padding: 1.25rem 1.5rem 1.5rem; background: #f8f8fb; }
.cim-range { margin-bottom: 1rem; }

/* ===== KPIs ===== */
.cim-kpis {
  display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.75rem; margin-bottom: 1rem;
}
.cim-kpi {
  position: relative;
  border-radius: 14px; padding: 0.85rem 1rem; color: #fff;
  box-shadow: 0 8px 18px -10px rgba(20, 24, 50, 0.35);

  &__label { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; opacity: 0.85; }
  &__value { font-size: 1.25rem; font-weight: 800; margin-top: 0.2rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__foot { font-size: 0.72rem; opacity: 0.85; margin-top: 0.15rem; }
  &__expand {
    position: absolute; top: 8px; right: 9px;
    font-size: 0.66rem; opacity: 0;
    transition: opacity 0.18s ease, transform 0.18s ease;
  }

  &--click {
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 14px 26px -12px rgba(20, 24, 50, 0.5);
      .cim-kpi__expand { opacity: 0.9; transform: scale(1.15); }
    }
  }

  &--dep { background: linear-gradient(135deg, #34d399, #059669); }
  &--wd  { background: linear-gradient(135deg, #fb7185, #dc2626); }
  &--net { background: linear-gradient(135deg, #60a5fa, #2563eb); }
  &--avg { background: linear-gradient(135deg, #a78bfa, #7c3aed); }
  &--bal { background: linear-gradient(135deg, #fbbf24, #d97706); }
}

/* ===== Panels ===== */
.cim-panel {
  background: #fff; border-radius: 14px; padding: 1rem 1.1rem; margin-bottom: 1rem;
  border: 1px solid rgba(110, 117, 159, 0.1);
  box-shadow: 0 6px 16px -12px rgba(20, 24, 50, 0.18);

  &__title { font-weight: 700; color: #5e5873; font-size: 0.95rem; margin-bottom: 0.75rem; }
  &__subtitle { font-weight: 700; color: #9a9dad; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.06em; margin: 0.9rem 0 0.4rem; }
  &__count {
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(115, 103, 240, 0.12); color: #7367f0;
    border-radius: 999px; min-width: 20px; height: 20px; padding: 0 6px;
    font-size: 0.7rem; font-weight: 800; margin-left: 0.4rem;
  }
}

/* ===== Journey ===== */
.cim-journey { position: relative; padding-left: 0.25rem; }
.cim-step {
  display: flex; gap: 0.8rem; position: relative; padding-bottom: 1rem;

  &:not(:last-child)::before {
    content: ''; position: absolute; left: 15px; top: 32px; bottom: 0;
    width: 2px; background: rgba(110, 117, 159, 0.18);
  }
  &__dot {
    width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
    display: inline-flex; align-items: center; justify-content: center;
    color: #fff; font-size: 0.8rem; z-index: 1;
  }
  &__label { font-weight: 700; color: #5e5873; font-size: 0.85rem; }
  &__extra {
    background: rgba(40, 199, 111, 0.12); color: #28c76f;
    border-radius: 999px; padding: 0.1rem 0.5rem; font-size: 0.72rem; margin-left: 0.3rem;
  }
  &__time { font-size: 0.74rem; color: #9a9dad; margin-top: 1px; }
  &__delta {
    background: rgba(115, 103, 240, 0.12); color: #7367f0;
    border-radius: 999px; padding: 0.05rem 0.45rem; font-size: 0.68rem; font-weight: 700; margin-left: 0.35rem;
  }
}

/* ===== People ===== */
.cim-people { display: flex; flex-direction: column; gap: 0.65rem; }
.cim-person {
  display: flex; align-items: center; gap: 0.7rem;

  &__avatar {
    width: 38px; height: 38px; border-radius: 12px; color: #fff;
    display: inline-flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 0.85rem; flex-shrink: 0;

    &--sales { background: linear-gradient(135deg, #28c76f, #1f9d57); }
    &--ret { background: linear-gradient(135deg, #7367f0, #5e50ee); }
    &--fin { background: linear-gradient(135deg, #ff9f43, #ff8510); }
  }
  &__role { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #9a9dad; }
  &__name { font-weight: 700; color: #5e5873; font-size: 0.9rem; }
}

/* ===== Split bars ===== */
.cim-split {
  display: flex; align-items: center; gap: 0.6rem; margin-bottom: 0.4rem;

  &__name { min-width: 120px; max-width: 140px; font-size: 0.78rem; font-weight: 600; color: #5e5873;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__barwrap { flex: 1; height: 8px; border-radius: 999px; background: rgba(110, 117, 159, 0.12); overflow: hidden; }
  &__bar { height: 100%; border-radius: 999px; background: linear-gradient(90deg, #7367f0, #9e95f5); }
  &__bar--green { background: linear-gradient(90deg, #28c76f, #48da8c); }
  &__val { min-width: 90px; text-align: right; font-size: 0.76rem; font-weight: 700; color: #5e5873; }
}

/* ===== Mini stats ===== */
.cim-mini-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.7rem; }
.cim-mini {
  background: rgba(115, 103, 240, 0.06); border-radius: 12px; padding: 0.7rem 0.8rem; text-align: center;

  &__value { font-weight: 800; color: #5e5873; font-size: 1rem; }
  &__label { font-size: 0.7rem; color: #9a9dad; margin-top: 0.1rem; }
}

/* ===== Notes ===== */
.cim-notes-scroll {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 0.25rem;
}
.cim-note {
  border-left: 3px solid rgba(115, 103, 240, 0.35);
  background: rgba(115, 103, 240, 0.04);
  border-radius: 0 10px 10px 0;
  padding: 0.55rem 0.75rem; margin-bottom: 0.55rem;

  &__head { display: flex; align-items: center; gap: 0.45rem; }
  &__author { font-weight: 700; font-size: 0.76rem; color: #5e5873; }
  &__flag { font-size: 0.7rem; color: #7367f0; &--imp { color: #ea5455; } }
  &__time { margin-left: auto; font-size: 0.68rem; color: #9a9dad; }
  &__text { font-size: 0.8rem; color: #6e6b7b; margin-top: 0.25rem; white-space: pre-wrap; word-break: break-word; }
}

/* ===== Dark mode ===== */
.cim--dark {
  .cim-body { background: #161d31; }
  .cim-panel { background: #283046; border-color: rgba(255, 255, 255, 0.06);
    &__title { color: #d0d2d6; }
  }
  .cim-step__label, .cim-person__name, .cim-split__name, .cim-split__val,
  .cim-mini__value, .cim-note__author { color: #d0d2d6; }
  .cim-note { background: rgba(115, 103, 240, 0.08); &__text { color: #b4b7bd; } }
  .cim-mini { background: rgba(115, 103, 240, 0.12); }
  .cim-split__barwrap { background: rgba(255, 255, 255, 0.08); }
  .cim-step:not(:last-child)::before { background: rgba(255, 255, 255, 0.1); }
}

@media (max-width: 1200px) { .cim-kpis { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 768px) {
  .cim-kpis { grid-template-columns: repeat(2, 1fr); }
  .cim-hero { flex-wrap: wrap; }
}
</style>
